import { computeTotals, formatBillNo, pointsFor, uuidv7, type Applied, type Bill, type BillLine, type MovementKind, type MovementMeta, type MovementLine, type Payment } from '@sense/shared';
import { currentCashier } from './cashier';
import type { CustomerRef } from './customers';
import { applyStock, billStockDeltas, db, nextCounter, setMeta } from './db';
import { useSession } from './session';
import { afterQueued, syncNow } from './sync';
import { isLocalOnly } from './local';
import { announcePayment } from './soundbox';

/** Bill number in the store's format (Settings → Bill number format). */
export function billNoFor(devNo: number, ts = Date.now()): string {
  const { me } = useSession.getState();
  return formatBillNo(me?.store.settings.billTemplate, { store: me?.store.store_no ?? '', device: me?.device.code ?? 'T01', seq: devNo, ts, start: me?.store.settings.billStart });
}

/** Save a finished bill locally (works offline), queue it for upload and update local stock. */
export async function saveBill(items: BillLine[], payments: Payment[], opts: { billDiscount: number; customer: CustomerRef | null; kind?: 0 | 1; offers?: Applied[]; approvals?: Array<[string, string]>; gstin?: string | null }): Promise<Bill> {
  const { me } = useSession.getState();
  const cashier = currentCashier();
  const t = computeTotals(items, opts.billDiscount);
  const kind = opts.kind ?? 0;
  let bill!: Bill;
  const now = Date.now();
  await db.transaction('rw', [db.meta, db.bills, db.outbox, db.stock, db.customers], async () => {
    const devNo = await nextCounter('devNo');
    bill = {
      id: uuidv7(),
      bill_no: (kind === 1 ? 'R' : '') + billNoFor(devNo, now),
      dev_no: devNo,
      ts: now,
      cashier: cashier.name,
      cashier_id: cashier.id,
      customer: opts.customer?.phone ?? null,
      customer_id: opts.customer?.id ?? null,
      kind,
      items,
      payments,
      subtotal: t.subtotal,
      discount: t.discount,
      tax: t.tax,
      round: t.round,
      total: t.total,
      ...(opts.offers?.length ? { offers: opts.offers.map((o) => [o.offerId, o.amount] as [number, number]) } : {}),
      ...(opts.approvals?.length ? { approvals: opts.approvals } : {}),
      ...(opts.gstin ? { gstin: opts.gstin } : {}),
    };
    await db.bills.put({ ...bill, synced: 0 });
    await db.outbox.add({ kind: 'bill', doc: bill });
    await applyStock(billStockDeltas(bill.items, kind));
    await setMeta('lastBillId', bill.id);
    // Show the new balance and points straight away; the server's numbers replace these on the next sync.
    if (bill.customer_id) {
      const c = await db.customers.get(bill.customer_id);
      if (c) {
        const sign = kind === 1 ? -1 : 1;
        const credit = payments.filter((p) => p[0] === 'credit').reduce((a, p) => a + p[1], 0) * sign;
        await db.customers.put({ ...c, balance: c.balance + credit, points: Math.max(0, c.points + sign * pointsFor(bill.total)), total_spent: c.total_spent + sign * bill.total, visits: c.visits + (kind === 0 ? 1 : 0), last_visit: bill.ts });
      }
    }
  });
  void afterQueued();
  announcePayment(bill); // soundbox: "Received 500 rupees by UPI"
  return bill;
}

/** Goods receipt, adjustment or wastage. Lines carry signed quantities. */
export async function saveMovement(kind: MovementKind, lines: MovementLine[], ref: string | null, note: string | null, meta?: MovementMeta) {
  const local = isLocalOnly();
  await db.transaction('rw', [db.meta, db.outbox, db.stock, db.batches], async () => {
    const devNo = await nextCounter('movNo');
    await db.outbox.add({ kind: 'movement', doc: { id: uuidv7(), dev_no: devNo, kind, ref, note, lines, ts: Date.now(), ...(meta ? { meta } : {}) } });
    await applyStock(lines.map(([pid, q]) => [pid, q]));
    // Phone-only: best-before batches are kept here (the server makes them for cloud stores).
    if (local && meta?.expiry?.length) {
      for (const [pid, expiry] of meta.expiry) {
        const qty = lines.filter(([p, q]) => p === pid && q > 0).reduce((a, [, q]) => a + q, 0);
        if (qty > 0) await db.batches.put({ id: -(Date.now() * 100 + Math.floor(Math.random() * 100)), product_id: pid, qty, expiry, created_at: Date.now() });
      }
    }
  });
  // Stock movements are rare and other terminals want them soon: upload right away.
  void syncNow();
}
